import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { siteImg, usePageHead } from "@/lib/siteContent";
import { API } from "@/lib/api";

const hideOnError = (e) => {
  e.currentTarget.style.display = "none";
};

// Public landing page for one brand we stock: every slab and remnant from it, live.
export default function SiteBrandStock() {
  const site = useSite();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    setMissing(false);
    fetch(`${API}/public/brands/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setMissing(true));
  }, [slug]);

  const brand = data?.brand;
  const ed = data?.editorial;
  usePageHead({
    title: brand ? ed?.seo_title || `${brand.name} slabs in stock` : "Brand",
    description: brand
      ? ed?.seo_description ||
        ed?.description ||
        `Browse every ${brand.name} slab and remnant on our racks right now.`
      : "",
  });

  if (missing) {
    return (
      <SiteShell site={site} testId="site-brand-stock-missing">
        <main className="px-6 py-28 text-center lg:px-12" data-testid="brand-stock-missing">
          <h1 className="s-display text-3xl sm:text-4xl">Brand not found</h1>
          <p className="mx-auto mt-4 max-w-md text-[color:var(--s-mute)]">
            We may not stock this brand any more. Browse everything on our racks instead.
          </p>
          <Link to="/inventory" className="s-btn mt-7 inline-flex" data-testid="brand-stock-back">
            Browse the inventory
          </Link>
        </main>
      </SiteShell>
    );
  }

  const items = data?.items || [];
  const stats = data?.stats || {};
  const heroImage = ed?.hero_image || items.find((i) => i.photo)?.photo;
  const heroSrc = ed?.hero_image
    ? siteImg(ed.hero_image)
    : heroImage
      ? `${API}/public/showroom/photo/${heroImage}?w=1800`
      : "";

  return (
    <SiteShell site={site} testId={`site-brand-stock-${slug}`}>
      <section className="relative overflow-hidden" data-testid="brand-stock-hero">
        {heroSrc ? (
          <>
            <img
              src={heroSrc}
              alt={brand?.name || ""}
              onError={hideOnError}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/35" />
          </>
        ) : (
          <div className="absolute inset-0 bg-[color:var(--s-ink)]" />
        )}
        <div className="relative mx-auto flex min-h-[52vh] max-w-[2560px] flex-col justify-end px-6 pb-12 pt-32 sm:min-h-[60vh] sm:pb-16 lg:px-12">
          <div className="s-rise max-w-3xl">
            <span className="s-micro inline-flex items-center gap-2 text-white/70">
              <span className="inline-block h-2 w-2 rotate-45 bg-[color:var(--s-gold)]" />
              {ed?.eyebrow || "Brands we stock"}
            </span>
            <div className="mt-5 flex flex-wrap items-end gap-5">
              {brand?.logo && (
                <img
                  src={siteImg(brand.logo)}
                  alt=""
                  onError={hideOnError}
                  data-testid="brand-stock-logo"
                  className="h-12 w-auto max-w-[12rem] object-contain brightness-0 invert sm:h-16"
                />
              )}
              <h1
                className="s-display text-4xl leading-[0.98] text-white sm:text-5xl lg:text-6xl"
                data-testid="brand-stock-headline"
              >
                {brand?.name || "…"}
              </h1>
            </div>
            {(ed?.tagline || ed?.description) && (
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
                {ed.tagline || ed.description}
              </p>
            )}
            {brand && (
              <p className="mt-4 text-sm text-white/70" data-testid="brand-stock-stats">
                {stats.slabs || 0} slab{stats.slabs === 1 ? "" : "s"} · {stats.remnants || 0}{" "}
                remnant{stats.remnants === 1 ? "" : "s"} · {stats.colors || 0} color
                {stats.colors === 1 ? "" : "s"} on the racks today
              </p>
            )}
          </div>
        </div>
      </section>

      <main className="px-6 pb-20 pt-12 sm:pb-28 sm:pt-16 lg:px-12">
        <section className="mx-auto max-w-[2560px]" data-testid="brand-stock-list">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {brand ? `${brand.name} on our racks` : "In stock"}
            </h2>
            <Link
              to="/inventory"
              className="s-ghost"
              data-testid="brand-stock-all-inventory"
            >
              Browse all inventory
            </Link>
          </div>

          {data && items.length === 0 && (
            <p
              className="mt-8 max-w-xl text-[color:var(--s-mute)]"
              data-testid="brand-stock-empty"
            >
              Nothing from {brand?.name} is on the racks right now — we can order any
              color in the line.{" "}
              <Link to="/contact" className="underline">
                Ask us for a quote
              </Link>
              .
            </p>
          )}

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((i, n) => (
              <Link
                key={i.key}
                to={`/inventory/${i.key}`}
                data-testid={`brand-stock-tile-${i.key}`}
                className="s-rise s-card group flex flex-col !p-1.5"
                style={{ animationDelay: `${Math.min(n, 9) * 60}ms` }}
              >
                <div className="s-fig s-card-fig relative aspect-[5/4] overflow-hidden">
                  {i.photo ? (
                    <img
                      src={`${API}/public/showroom/photo/${i.thumb || i.photo}?w=700`}
                      alt={i.color}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-[color:var(--s-mute)]">
                      Photo coming soon
                    </div>
                  )}
                  <span className="absolute left-3 top-3 flex flex-wrap gap-2">
                    {i.kind === "Remnant" && (
                      <span className="s-pill s-pill-dark">Remnant</span>
                    )}
                    {i.material && <span className="s-pill">{i.material}</span>}
                  </span>
                </div>
                <div className="px-2.5 pb-3 pt-3">
                  <div className="s-display text-lg leading-tight">{i.color}</div>
                  <div className="mt-1 text-xs text-[color:var(--s-mute)]">
                    {[i.finish, i.thickness, i.size].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </SiteShell>
  );
}
